<template>
  <div class="card mb-3" data-cy="schedule-card">
    <div class="card-body d-flex justify-content-between align-items-start">
      <div>
        <div class="d-flex align-items-center mb-1">
          <h5 class="mb-0">{{ schedule.name }}</h5>
          <Badge
            v-if="schedule.system_default"
            class="badge-sm"
            text="System"
            color="blue"
            tooltip-text="System default schedule"
          />
          <Badge
            v-if="!schedule.is_active"
            class="badge-sm"
            text="Inactive"
            color="yellow"
            tooltip-text="This schedule is deactivated"
          />
        </div>
        <p v-if="schedule.description" class="text-muted mb-1 small">
          {{ schedule.description }}
        </p>
        <div class="small text-muted">
          <span v-for="(inst, idx) in sortedInstallments" :key="idx">
            {{ inst.label }} ({{ inst.percentage }}%)
            <span v-if="idx < sortedInstallments.length - 1"> &rarr; </span>
          </span>
        </div>
      </div>
      <div class="d-flex gap-3 align-items-center">
        <span
          title="Edit"
          data-cy="edit-schedule"
          @click="$emit('edit', schedule)"
        >
          <Pencil class="link" :size="16" />
        </span>
        <span
          v-if="schedule.is_active"
          title="Duplicate"
          data-cy="copy-schedule"
          @click="$emit('copy', schedule)"
        >
          <Copy class="link" :size="16" />
        </span>
        <span
          v-if="!schedule.is_active"
          title="Activate"
          data-cy="unarchive-schedule"
          @click="$emit('unarchive', schedule)"
        >
          <ArchiveRestore class="link text-success" :size="16" />
        </span>
        <span
          v-if="schedule.is_active"
          title="Deactivate"
          data-cy="deactivate-schedule"
          @click="$emit('deactivate', schedule)"
        >
          <Archive class="link text-danger" :size="16" />
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Archive, ArchiveRestore, Copy, Pencil } from "lucide-vue-next";
import { computed } from "vue";

import Badge from "@/components/Badge.vue";

interface Installment {
  id: number;
  label: string;
  percentage: number;
  position: number;
}

interface Schedule {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  system_default: boolean;
  installments: Installment[];
}

const props = defineProps<{
  schedule: Schedule;
}>();

defineEmits<{
  edit: [schedule: Schedule];
  copy: [schedule: Schedule];
  unarchive: [schedule: Schedule];
  deactivate: [schedule: Schedule];
}>();

const sortedInstallments = computed(() =>
  [...props.schedule.installments].sort((a, b) => a.position - b.position)
);
</script>

<style lang="scss" scoped>
:deep(.badge-sm) {
  height: 20px;
  font-size: 11px;
  padding: 0 5px;
}
</style>
